import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Divider,
  Grid,
  Button,
  Collapse,
} from '@mui/material';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

function formatCurrency(v) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (Number.isFinite(n)) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n);
    } catch {
      return `£${n.toFixed(2)}`;
    }
  }
  return '—';
}

function formatUnitPrice(v, unit) {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return unit === 'L' ? `£${n.toFixed(3)}/L` : formatCurrency(n);
}

const ReceiptItem = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  const name = item.proper_name ?? item.short_name ?? '—';
  const qty = item.quantity ?? 1;
  const ppu = item.price_per_unit;
  const t = item.total_price;
  const cat = item.category ?? 'Other';
  const unit = item.unit ?? null;
  const fuelGrade = item.fuel_grade ?? null;
  const discounted = item.is_discounted;

  return (
    <Box
      sx={{
        p: 2,
        cursor: 'pointer',
        ...(discounted && { backgroundColor: 'rgba(255, 235, 59, 0.1)' }),
      }}
      onClick={() => setExpanded(prev => !prev)}
    >
      <Grid container alignItems="center" spacing={1}>
        <Grid item size={{ xs: 8 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 500 }}>{name}</Typography>            
          </Stack>
        </Grid>
        <Grid item size={{ xs: 4 }} sx={{ textAlign: 'right' }}>          
          <Typography sx={{ fontWeight: 500 }}>            
            {formatCurrency(t)}
          </Typography>
        </Grid>
      </Grid>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1.5, pl: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Category: {cat}
          </Typography>
          {fuelGrade && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Fuel grade: {fuelGrade}
            </Typography>
          )}
          <Grid container spacing={1}>
            <Grid item size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary">Qty</Typography>
              <Typography variant="body2">{qty}{unit ? ` ${unit}` : ''}</Typography>
            </Grid>
            <Grid item size={{ xs: 6 }} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                {unit === 'L' ? 'Price per litre' : 'Price per item'}
              </Typography>
              <Typography variant="body2">{formatUnitPrice(ppu, unit)}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

const ReceiptCard = ({ receipt }) => {
  // This state is local to each instance of ReceiptCard
  const [showImage, setShowImage] = useState(false);

  const storeName = receipt.store_name ?? 'Unknown Store';
  const dateStr = receipt.transaction_date ?? null;
  const timeStr = receipt.transaction_time ?? null;
  const total = receipt.total_amount ?? null;
  
  const items = [...(receipt.items ?? [])].sort((a, b) => {
    const nameA = a.proper_name ?? a.short_name ?? '';
    const nameB = b.proper_name ?? b.short_name ?? '';
    return nameA.localeCompare(nameB);
  });

  const displayDate = new Date(dateStr);
  if (timeStr) {
    const [hours, minutes, seconds] = timeStr.split(':');
    displayDate.setHours(hours, minutes, seconds);
  }

  return (
    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{storeName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {timeStr ? timeStr : 'Date not available'}
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 1 }}>
          Total: {formatCurrency(total)}
        </Typography>
        {receipt.receipt_image_url && (
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Button size="small" onClick={() => setShowImage(prev => !prev)}>
              {showImage ? 'Hide Receipt' : 'Show Receipt'}
            </Button>
            <Collapse in={showImage}>
              <Box sx={{ pt: 2 }}>
                <img
                  src={receipt.receipt_image_url}
                  alt="Receipt"
                  style={{ display: 'block', maxWidth: '100%', width: '300px', borderRadius: 8 }}
                  onClick={() => window.open(receipt.receipt_image_url, '_blank', 'noopener,noreferrer')}
                />
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Items</Typography>
        {items.length === 0 ? (
          <Alert severity="info">No items on this receipt.</Alert>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {items.map((it, idx) => (
              <ReceiptItem key={it.id ?? idx} item={it} />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default function ReceiptDetailPage() {
  const { date } = useParams();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${backendUrl}/receipts/by-date/${date}`);
        if (!res.ok) {
          const msg = `Failed to load receipts: ${res.status}`;
          throw new Error(msg);
        }
        const data = await res.json();
        if (!ignore) setReceipts(data);
      } catch (e) {
        if (!ignore) setErr(e.message || 'Failed to load receipts.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    if (date) {
      load();
    } else {
      setErr("No date provided in URL.");
      setLoading(false);
    }
    return () => { ignore = true; };
  }, [date]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (err) {
    return <Alert severity="error">{err}</Alert>;
  }

  if (!receipts || receipts.length === 0) {
    return <Alert severity="info">No receipts found for this date.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        Receipts for {new Date(date).toLocaleDateString()}
      </Typography>
      {receipts.map(receipt => (
        <ReceiptCard key={receipt.id} receipt={receipt} />
      ))}
    </Stack>
  );
}
